// Pause a CSS-animated element when it leaves the viewport.
// Web Animations API: every animation on the node is pause()/play()'d.
// No-op if reduced motion is preferred (no animations anyway).
export function pauseWhenOffscreen(node: Element) {
  if (typeof window === 'undefined') return {};
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return {};
  if (!('IntersectionObserver' in window) || !node.getAnimations) return {};

  const apply = (play: boolean) => {
    // getAnimations on SVG group returns its CSS animation(s).
    for (const a of node.getAnimations()) {
      if (play) a.play();
      else a.pause();
    }
  };

  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) apply(e.isIntersecting);
    },
    // 0% = paused as soon as fully gone; small margin so brief scroll-bounce
    // doesn't toggle pause rapidly.
    { threshold: 0, rootMargin: '20px' },
  );
  io.observe(node);
  return {
    destroy() {
      io.disconnect();
    },
  };
}

// Reveal-on-scroll Svelte action. Mirrors the original IntersectionObserver:
// element starts with `.reveal` (opacity 0, translateY 22px); when 10% of it
// enters the viewport, add `.seen` and unobserve.
//
// Usage:  <section class="reveal" use:reveal>

export function reveal(node: HTMLElement) {
  const io = new IntersectionObserver(
    (entries) => {
      for (const en of entries) {
        if (en.isIntersecting) {
          en.target.classList.add('seen');
          io.unobserve(en.target);
        }
      }
    },
    { threshold: 0.1 },
  );
  io.observe(node);
  return {
    destroy() {
      io.disconnect();
    },
  };
}

// Pointer parallax — tilts the node slightly toward the cursor while hovered.
// Cheap: one rAF per move, transform-only (no layout), removed on leave.
// Skipped entirely under prefers-reduced-motion.
//
// Amplitude is small (±2.5° rotate + ±3px translate) so it reads as "alive",
// not as a toy. The card's existing hover transform (translateY(-3px)) is
// preserved — we add ours on a child wrapper.
export function tilt(node: HTMLElement) {
  if (typeof window === 'undefined') return {};
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return {};

  const MAX_ANGLE = 2.5;
  const MAX_SHIFT = 3;
  let raf = 0;
  let targetRX = 0;
  let targetRY = 0;
  let targetTX = 0;
  let targetTY = 0;
  let curRX = 0;
  let curRY = 0;
  let curTX = 0;
  let curTY = 0;

  function onMove(ev: PointerEvent) {
    const r = node.getBoundingClientRect();
    // Cursor position as -1..1 from center.
    const nx = ((ev.clientX - r.left) / r.width) * 2 - 1;
    const ny = ((ev.clientY - r.top) / r.height) * 2 - 1;
    targetRX = -ny * MAX_ANGLE;
    targetRY = nx * MAX_ANGLE;
    targetTX = nx * MAX_SHIFT;
    targetTY = ny * MAX_SHIFT;
    if (!raf) raf = requestAnimationFrame(tick);
  }

  function onLeave() {
    targetRX = targetRY = targetTX = targetTY = 0;
    if (!raf) raf = requestAnimationFrame(tick);
  }

  function tick() {
    // Exponential ease toward target. The factor controls "weight" — lower
    // feels heavier / more deliberate.
    curRX += (targetRX - curRX) * 0.18;
    curRY += (targetRY - curRY) * 0.18;
    curTX += (targetTX - curTX) * 0.18;
    curTY += (targetTY - curTY) * 0.18;

    const settled =
      Math.abs(targetRX - curRX) < 0.01 &&
      Math.abs(targetRY - curRY) < 0.01 &&
      Math.abs(targetTX - curTX) < 0.05 &&
      Math.abs(targetTY - curTY) < 0.05;

    node.style.setProperty('--tilt-rx', `${curRX.toFixed(2)}deg`);
    node.style.setProperty('--tilt-ry', `${curRY.toFixed(2)}deg`);
    node.style.setProperty('--tilt-tx', `${curTX.toFixed(2)}px`);
    node.style.setProperty('--tilt-ty', `${curTY.toFixed(2)}px`);

    if (settled) {
      raf = 0;
      return;
    }
    raf = requestAnimationFrame(tick);
  }

  node.style.transformStyle = 'preserve-3d';
  node.addEventListener('pointermove', onMove);
  node.addEventListener('pointerleave', onLeave);
  node.addEventListener('pointercancel', onLeave);

  return {
    destroy() {
      node.removeEventListener('pointermove', onMove);
      node.removeEventListener('pointerleave', onLeave);
      node.removeEventListener('pointercancel', onLeave);
      if (raf) cancelAnimationFrame(raf);
    },
  };
}
